import React, { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import confetti from "canvas-confetti";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Cake, Gift, Heart, Sparkles, Quote } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/store/auth";

const fire = (palette = ["#1E96FC", "#003771", "#ffffff", "#FFD700"]) => {
  confetti({ particleCount: 80, spread: 70, origin: { y: 0.6 }, colors: palette });
  setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { x: 0.2, y: 0.5 }, colors: palette }), 200);
  setTimeout(() => confetti({ particleCount: 80, spread: 100, origin: { x: 0.8, y: 0.5 }, colors: palette }), 400);
};

const themes = {
  birthday:           { icon: Cake,     palette: ["#FF6B9D", "#FFD700", "#1E96FC", "#16A34A"], gradient: "linear-gradient(135deg, #ec4899, #1E96FC)" },
  work_anniversary:   { icon: Sparkles, palette: ["#1E96FC", "#003771", "#FFD700"],            gradient: "linear-gradient(135deg, #003771, #1E96FC)" },
  wedding_anniversary:{ icon: Heart,    palette: ["#FF6B9D", "#ffffff", "#FFD700"],            gradient: "linear-gradient(135deg, #ec4899, #f59e0b)" },
  festival:           { icon: Gift,     palette: ["#f59e0b", "#ec4899", "#1E96FC"],            gradient: "linear-gradient(135deg, #f59e0b, #ec4899)" },
  quote:              { icon: Quote,    palette: [],                                            gradient: "linear-gradient(135deg, #003771, #1E96FC)" },
};

function DelightModal({ kind, title, body, footer, onClose, fireOnOpen = true }) {
  // D17 Issue 3 -- the daily-quote opt-out has been removed by policy.
  // The modal renders exactly the same for every kind (quote / birthday /
  // work_anniversary / wedding_anniversary / festival). The once-per-day
  // throttle upstream stays intact.
  const T = themes[kind];
  const Icon = T?.icon || Sparkles;
  useEffect(() => {
    if (fireOnOpen && T?.palette?.length) fire(T.palette);
  }, [fireOnOpen, T]);

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-md p-0 overflow-hidden border-white/10 bg-[var(--surface-elevated)] backdrop-blur-2xl"
        data-testid={`delight-modal-${kind}`}
      >
        {/* D22 -- Radix a11y requires a Title + Description; the visible
            <h2> below is the real title, but Radix wants a DialogTitle
            binding for its aria-labelledby / aria-describedby wiring. */}
        <VisuallyHidden.Root>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </VisuallyHidden.Root>
        <div className="relative">
          <div className="absolute inset-0 opacity-30" style={{ background: T?.gradient }} />
          <div className="relative p-6 sm:p-8 text-center">
            <div className="mx-auto w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg" style={{ background: T?.gradient }}>
              <Icon className="h-8 w-8 text-white" />
            </div>
            <h2 className="font-heading text-2xl tracking-[0.12em] font-bold uppercase">{title}</h2>
            <p className="mt-3 text-sm text-foreground/80 font-body leading-relaxed">{body}</p>
            {footer && <div className="mt-4 text-xs text-foreground/55 italic">{footer}</div>}
            <Button
              onClick={onClose}
              className="mt-6 h-10 px-6 bg-gradient-primary text-white font-heading tracking-[0.18em]"
              data-testid={`delight-close-${kind}`}
            >
              CONTINUE
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const ORDER = ["birthday", "work_anniversary", "wedding_anniversary", "festival", "quote"];

export function DelightProvider({ children }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [bundle, setBundle] = useState(null);
  const [seen, setSeen] = useState({});
  const [queue, setQueue] = useState([]);
  const [active, setActive] = useState(null);
  // D17 Issue 3 -- opt-out state removed. The quote fires once per day
  // regardless of any past client preference (which is now server-ignored).
  // See handleClose below.

  const load = useCallback(async () => {
    if (!user) return;
    try {
      const [t1, t2] = await Promise.all([
        api.get("/delight/today"),
        api.get("/delight/seen-today"),
      ]);
      const b = t1.data;
      if (!b?.enabled) return;
      setBundle(b);
      const s = t2.data?.seen || {};
      setSeen(s);
      // Build queue in priority order. The daily quote ALWAYS enqueues if
      // present + not-yet-seen-today (opt-out policy removed in D17).
      const q = ORDER.filter((k) => {
        if (!b.items?.[k]) return false;
        if (s[k]) return false;
        return true;
      });
      setQueue(q);
      if (q.length) setActive(q[0]);
    } catch (_e) {
      // silent -- delight is non-critical
    }
  }, [user]);

  useEffect(() => { load(); }, [load]);

  const handleClose = async () => {
    if (!active) return;
    try { await api.post("/delight/seen", { kind: active }); } catch (_e) { /* silent */ }
    setSeen((s) => ({ ...s, [active]: true }));
    const rest = queue.filter((k) => k !== active);
    setQueue(rest);
    setActive(rest[0] || null);
  };

  // D17 Issue 3 -- the daily-quote opt-out has been removed by policy.
  // The old handler + gate are gone; the once-per-day throttle upstream
  // stays intact.

  if (!active || !bundle?.items?.[active]) return children;

  const item = bundle.items[active];
  let title = "", body = "", footer = "";
  if (active === "birthday") {
    title = t("delight.happyBirthday");
    body = t("delight.birthdayBody", { name: item.name });
  } else if (active === "work_anniversary") {
    title = t("delight.happyWorkAnn", { years: item.years });
    body = t("delight.workAnnBody", { years: item.years, name: item.name });
  } else if (active === "wedding_anniversary") {
    title = t("delight.happyAnniv");
    body = t("delight.annivBody", { years: item.years, name: item.name });
  } else if (active === "festival") {
    title = `${item.emoji || "🎉"} ${t("delight.festival", { name: item.label_en })}`;
    body = item.label_hi || item.label_en;
  } else if (active === "quote") {
    title = t("delight.thought");
    body = `"${item.text}"`;
    footer = `— ${item.author || "Unknown"}`;
  }

  return (
    <>
      {children}
      {/* D15 -- plain Shadcn <Dialog> (native transition). Framer's
          animation wrapper was intercepting pointer-events during exit
          animation and blocking clicks on the page underneath. Shadcn's
          own overlay handles fade in/out cleanly and respects
          `onOpenChange` for outside clicks + ESC. */}
      <DelightModal
        kind={active}
        title={title}
        body={body}
        footer={footer}
        onClose={handleClose}
      />
    </>
  );
}

export default DelightProvider;
